import {Mapper} from '@s-ui/domain'

import {DealerProfileRatingsValueObject} from '../../ratings/ValueObjects/DealerProfileRatingsValueObject.js'
import {OfferPriceValueObject} from '../ValueObjects/OfferPriceValueObject.js'
import {PriceDropValueObject} from '../ValueObjects/PriceDropValueObject.js'

/**
 * @implements Mapper
 */
export class FromAdRawDataToAdEntityMapper extends Mapper {
  constructor({adEntityFactory, packDealerEntity}) {
    super()
    // Entities
    this._adEntityFactory = adEntityFactory
    this._packDealerEntity = packDealerEntity
    this._compareNumber = (a, b) => {
      return a - b
    }
  }

  _getPhotos(resources = []) {
    return resources.map(resource => resource.type === 'IMAGE' && resource.url)
  }

  _getSubscriptionData(rawAd) {
    const {subscriptionData = {}, transmissionTypeId} = rawAd

    const commitmentsConfigurations = subscriptionData.commitmentsConfigurations

    if (!commitmentsConfigurations) return

    const {selected: selectedCommitment, otherOptions: otherCommitmentOptions} = commitmentsConfigurations

    const {kmIncludedPeriod, kmIncluded, targetBuyer} = selectedCommitment

    const commitmentMonthlyOptions = Array.from(new Set(otherCommitmentOptions.map(option => option.totalMonths))).sort(
      this._compareNumber
    )

    const hasNoCommitmentOption = [selectedCommitment, ...otherCommitmentOptions]
      .map(option => option.totalMonths)
      .includes(0)

    return {
      commitmentMonthlyOptions,
      hasNoCommitmentOption,
      isSubscription: true,
      kmIncluded,
      kmIncludedPeriod,
      monthlySubscriptionCommitment: selectedCommitment.totalMonths,
      monthlySubscriptionFee: selectedCommitment.monthlyPrice,
      subscriptionVehicleState: subscriptionData.vehicleState,
      targetBuyer,
      transmissionTypeId
    }
  }

  _getPriceDrop = ({price}) => {
    const priceDropVO = PriceDropValueObject.createFromApi({price})
    return priceDropVO?.value
  }

  map = rawAd => {
    const hasPrice = Boolean(rawAd.price)
    const hasResources = Boolean(rawAd.resources) && rawAd.resources.length > 0
    const hasWarranty = Boolean(rawAd.warranty)
    const hasStock = Boolean(rawAd.hasStock)
    const hasSubscriptionData = Boolean(rawAd.subscriptionData)
    const hasPack = Boolean(rawAd.pack)
    const hasPriceIndicator = Boolean(rawAd.price.indicator)
    const hasPriceOffer = Boolean(rawAd.price.offer)
    const hasRatings = Boolean(rawAd.seller?.ratings)
    const hasEnvironmentalLabel = Boolean(rawAd.environmentalLabel)
    const hasHp = Boolean(rawAd.hp)

    const ratingsValueObject = DealerProfileRatingsValueObject.createFromApiResponse({
      commentsNumber: rawAd.seller?.ratings?.commentsNumber,
      scoreAverage: rawAd.seller?.ratings?.scoreAverage
    })

    const offer =
      hasPriceOffer &&
      OfferPriceValueObject.createFromApi({
        offerPrice: rawAd.price.offer?.offerPrice,
        manufacturerPrice: rawAd.price.offer?.manufacturerPrice || rawAd.price.amount
      })

    return this._adEntityFactory({
      ...(hasEnvironmentalLabel && {environmentalLabel: rawAd.environmentalLabel}),
      ...(hasPrice && {
        price: rawAd.price.amount,
        ...(hasPriceIndicator && {
          priceAverageIndicator: rawAd.price.indicator.average,
          priceRankIndicator: rawAd.price.indicator.rank
        }),
        ...(hasPriceOffer && {
          offer: {
            offerPrice: offer?.offerPrice,
            manufacturerPrice: offer?.manufacturerPrice,
            discountPercentage: offer?.getDiscountPercentageLiteral()
          }
        }),
        financedPrice: rawAd.price.financedAmount,
        financingInfo: rawAd.price.financingInfo || undefined,
        hasOnlineFinancing: rawAd.price.hasOnlineFinancing,
        hasReservation: rawAd.price.hasReservation,
        includesTaxes: rawAd.price.hasTaxes,
        taxTypeId: rawAd.price.taxTypeId
      }),
      ...(hasResources && {
        img: rawAd.resources[0].url,
        photos: this._getPhotos(rawAd.resources)
      }),
      ...(hasSubscriptionData && this._getSubscriptionData(rawAd)),
      ...(hasWarranty && {
        warrantyId: rawAd.warranty.id,
        warrantyMonths: rawAd.warranty.months,
        warrantyText: rawAd.warranty.literal
      }),
      ...(rawAd.hasStock && {hasStock}),
      ...(hasHp && {hp: rawAd.hp}),
      videos: rawAd.videos,
      bodyTypeId: rawAd.bodyTypeId,
      creationDate: rawAd.creationDate,
      description: rawAd.description,
      fuelType: rawAd.fuelType,
      fuelTypeId: rawAd.fuelTypeId,
      hasWarranty,
      id: rawAd.id,
      isCertified: rawAd.isCertified,
      isFinanced: rawAd.isFinanced,
      isProfessional: rawAd.isProfessional,
      isTop: rawAd.isTop,
      isUrgent: rawAd.hasUrge,
      km: rawAd.km,
      location: rawAd.location,
      offerType: rawAd.offerType,
      category1Id: rawAd.category1Id,
      ...(hasPack && {
        pack: this._packDealerEntity({
          legacyId: rawAd.pack.legacyId,
          type: rawAd.pack.type
        })
      }),
      phone: rawAd.phone,
      hidePhone: rawAd.hidePhone,
      provinceId: rawAd.provinceIds?.[0],
      priceDrop: this._getPriceDrop({price: rawAd.price}),
      publicationDate: rawAd.publishedDate,
      // TODO: Create Seller Value Object --> https://jira.ets.mpi-internal.com/browse/MTR-85719
      ...(rawAd.isProfessional && {
        seller: {
          ...rawAd.seller,
          ...(hasRatings && {ratings: ratingsValueObject.value})
        }
      }),
      specificFuelTypeId: rawAd.fuelTypeId,
      title: rawAd.title,
      url: rawAd.url,
      year: rawAd.year,
      make: rawAd.make,
      model: rawAd.model,
      makeId: rawAd.makeId,
      modelId: rawAd.modelId
    })
  }
}
